import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Compass, Route } from "lucide-react";
import {
  ExploreSearchPanel,
  type ExploreSearchPanelProps,
} from "@/components/explore-search-panel";
import {
  RouteSearchPanel,
  type RouteSearchPanelProps,
} from "@/components/route-search-panel";
import { cn } from "@trotroways/utils";
import { Logo } from "@trotroways/ui";

export type SearchMode = "explore" | "route";

interface SearchCardProps {
  mode: SearchMode;
  onModeChange: (mode: SearchMode) => void;
  exploreProps: ExploreSearchPanelProps;
  routeProps: RouteSearchPanelProps;
}

function ModeToggle({
  mode,
  onModeChange,
}: {
  mode: SearchMode;
  onModeChange: (mode: SearchMode) => void;
}) {
  return (
    <div className="flex gap-1 rounded-xl bg-[#0004] p-1">
      <button
        type="button"
        onClick={() => onModeChange("route")}
        className={cn(
          "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold",
          "transition-all duration-200",
          mode === "route"
            ? "bg-[#2b2b2b]/50 text-white/90 shadow-sm"
            : "text-zinc-400 hover:text-zinc-600",
        )}
      >
        <Route className="size-3.5" />
        Route
      </button>
      <button
        type="button"
        disabled
        className={cn(
          "flex flex-1 cursor-not-allowed items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold",
          "transition-all duration-200",
          "text-zinc-400",
        )}
      >
        <Compass className="size-3.5" />
        Explore
      </button>
    </div>
  );
}

export const SearchCard = memo(function SearchCard({
  mode,
  onModeChange,
  exploreProps,
  routeProps,
}: SearchCardProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );

  const measureHeight = useCallback(() => {
    if (!contentRef.current) return;
    const activePanel = contentRef.current.querySelector(
      `[data-mode='${mode}']`,
    );
    if (activePanel) {
      setContentHeight(activePanel.scrollHeight);
    }
  }, [mode]);

  useEffect(() => {
    measureHeight();
  }, [measureHeight]);

  useEffect(() => {
    if (!contentRef.current) return;
    const activePanel = contentRef.current.querySelector(
      `[data-mode='${mode}']`,
    );
    if (!activePanel) return;

    const observer = new ResizeObserver(() => {
      measureHeight();
    });
    observer.observe(activePanel);

    return () => observer.disconnect();
  }, [measureHeight, mode]);

  return (
    <div
      className={cn(
        "md:rounded-2xl ",
        "md:bg-[#0009] md:backdrop-blur-[50px]",
        "shadow-xl shadow-black/8",
      )}
    >

      <div className="px-4 py-5  ">
        <Logo className="text-white w-28 h-5" />
      </div>

      <div className="p-3 pb-0">
        <ModeToggle mode={mode} onModeChange={onModeChange} />
      </div>

      <div
        className="relative transition-[height] duration-300 ease-in-out"
        style={{ height: contentHeight !== undefined ? contentHeight : "auto" }}
      >
        <div ref={contentRef}>
          <div
            data-mode="explore"
            className={cn(
              "p-3 transition-opacity duration-200 ease-in-out",
              mode === "explore"
                ? "relative opacity-100"
                : "pointer-events-none absolute inset-x-0 top-0 opacity-0",
            )}
          >
            <ExploreSearchPanel {...exploreProps} />
          </div>

          <div
            data-mode="route"
            className={cn(
              "p-3 transition-opacity duration-200 ease-in-out",
              mode === "route"
                ? "relative opacity-100"
                : "pointer-events-none absolute inset-x-0 top-0 opacity-0",
            )}
          >
            <RouteSearchPanel {...routeProps} />
          </div>
        </div>
      </div>
    </div>
  );
});
